import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Printer, ArrowRight, Truck, User, Package, IndianRupee, ShieldCheck } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

const VEHICLE_TYPES = ["Mini Truck (Tata Ace)", "Pickup (7ft)", "LCV 14ft", "17ft Truck", "19ft Truck", "22ft Truck", "32ft SXL Container", "32ft MXL Container", "Trailer", "Bus", "Car"];

export default function OpsDashboard() {
  const [trips, setTrips] = useState([]);
  const [overview, setOverview] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchAll = async () => {
    try { const { data } = await api.get("/trips"); setTrips(data.trips); }
    catch (e) { /* silent */ }
    try { const { data } = await api.get("/ops/overview"); setOverview(data); }
    catch (e) { /* silent */ }
    try { const { data } = await api.get("/vehicles"); setVehicles(data.vehicles); }
    catch (e) { /* silent */ }
    try { const { data } = await api.get("/customers"); setCustomers(data.customers || []); }
    catch (e) { /* silent */ }
    try { const { data } = await api.get("/admin/users?role=driver"); setDrivers(data.users || []); }
    catch (e) { /* loading_supervisor doesn't have admin scope; that's fine */ }
  };
  useEffect(() => { fetchAll(); }, []);

  const drafts = useMemo(() => trips.filter(t => t.status === "draft"), [trips]);
  const active = useMemo(() => trips.filter(t => ["approved", "loaded", "in_transit"].includes(t.status)), [trips]);
  const delivered = useMemo(() => trips.filter(t => ["delivered", "closed"].includes(t.status)), [trips]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-[#FF5A00]">OPERATIONS · LOADING SUPERVISION</div>
            <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="ops-dashboard">Feed the trips. Move the yard.</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-trip-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-12 px-5">
                <Plus className="w-5 h-5 mr-2" /> New Trip / CN
              </Button>
            </DialogTrigger>
            <NewTripDialog onDone={() => { setOpen(false); fetchAll(); }} vehicles={vehicles} drivers={drivers} customers={customers} onCustomersRefresh={fetchAll} />
          </Dialog>
        </div>

        {overview && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Draft (awaiting approval)" value={overview.trips.draft} accent />
            <Stat label="Approved / active" value={overview.trips.approved + overview.trips.in_transit} />
            <Stat label="Fleet vehicles" value={overview.fleet.total} />
            <Stat label="Drivers registered" value={overview.drivers} />
          </div>
        )}

        <Tabs defaultValue="drafts" className="mt-10">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="drafts" data-testid="ops-tab-drafts" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Drafts ({drafts.length})</TabsTrigger>
            <TabsTrigger value="active" data-testid="ops-tab-active" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Active ({active.length})</TabsTrigger>
            <TabsTrigger value="delivered" data-testid="ops-tab-delivered" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Delivered ({delivered.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="drafts" className="mt-6"><TripTable trips={drafts} /></TabsContent>
          <TabsContent value="active" className="mt-6"><TripTable trips={active} /></TabsContent>
          <TabsContent value="delivered" className="mt-6"><TripTable trips={delivered} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-[#2A2A2A] bg-[#141414]"}`}>
      <div className="overline text-zinc-400">{label}</div>
      <div className={`font-display font-black text-3xl mt-2 ${accent ? "text-[#FF5A00]" : ""}`}>{value}</div>
    </div>
  );
}

function TripTable({ trips }) {
  if (!trips.length) return <div className="p-12 border border-dashed border-[#2A2A2A] text-center text-zinc-500">No trips here.</div>;
  return (
    <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">CN #</th>
            <th className="text-left p-3">Type</th>
            <th className="text-left p-3">Route</th>
            <th className="text-left p-3">Customer</th>
            <th className="text-left p-3">Vehicle · Driver</th>
            <th className="text-left p-3">Freight</th>
            <th className="text-left p-3">Status</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {trips.map((t) => (
            <tr key={t.id} data-testid={`ops-trip-row-${t.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60">
              <td className="p-3 font-mono-ll text-xs">{t.cn_number}</td>
              <td className="p-3 text-xs">
                {t.trip_type === "driver_placement" ? <span className="text-[#FFD700]">Driver Hire</span> : <span className="text-[#FF5A00]">Vehicle</span>}
              </td>
              <td className="p-3 font-mono-ll text-xs inline-flex items-center gap-2">
                <span>{t.source_city}</span><ArrowRight className="w-3 h-3 text-[#FF5A00]" /><span>{t.destination_city}</span>
              </td>
              <td className="p-3 text-xs"><div>{t.customer?.name}</div><div className="text-zinc-500">{t.customer?.phone}</div></td>
              <td className="p-3 text-xs">
                <div>{t.vehicle?.vehicle_number || "—"}</div>
                <div className="text-zinc-500">{t.driver?.name || "—"}</div>
              </td>
              <td className="p-3">{formatINR(t.money?.customer_freight || 0)}</td>
              <td className="p-3"><StatusBadge status={t.status === "in_transit" ? "in_transit" : t.status === "delivered" ? "delivered" : t.status === "closed" ? "delivered" : t.status === "approved" ? "confirmed" : "posted"} /></td>
              <td className="p-3 text-right">
                <div className="inline-flex gap-2">
                  <Link to={`/trips/${t.id}`}>
                    <Button size="sm" variant="outline" data-testid={`ops-view-${t.id}`} className="h-8 rounded-sm border-[#2A2A2A] bg-transparent hover:bg-white/5">Open</Button>
                  </Link>
                  <Link to={`/prints/lr/${t.id}`} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" data-testid={`ops-print-lr-${t.id}`} className="h-8 rounded-sm border-[#2A2A2A] bg-transparent hover:bg-[#FF5A00]/10 text-[#FF5A00]">
                      <Printer className="w-3 h-3 mr-1" /> LR
                    </Button>
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewTripDialog({ onDone, vehicles, drivers, customers, onCustomersRefresh }) {
  const [f, setF] = useState({
    trip_type: "vehicle_placement",
    customer_id: "",
    customer_name: "", customer_phone: "", customer_gstin: "",
    consignor_id: "", consignor: "", consignor_address: "", consignor_gstin: "",
    consignee_id: "", consignee: "", consignee_address: "", consignee_gstin: "",
    source_city: "", destination_city: "",
    material: "", weight_kg: "",
    invoice_number: "", sales_order_number: "",
    pickup_date: "", delivery_date: "",
    eway_bill_number: "", eway_bill_expiry: "",
    freight_type: "lumpsum", unit_price: "", units: "",
    own_vehicle_id: "",
    outsourced_vehicle_number: "", outsourced_lorry_owner_name: "", outsourced_lorry_owner_phone: "",
    driver_id: "", driver_name: "", driver_phone: "", driver_dl: "",
    customer_freight: "", lorry_hire_total: "", lorry_advance: "", driver_hire_total: "",
    remarks: "",
  });
  const [vehicleMode, setVehicleMode] = useState("own");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const activeCustomers = (customers || []).filter(c => c.contract_active);

  const onCustomerPick = (customerId) => {
    const c = (customers || []).find(x => x.id === customerId);
    if (!c) return setF(s => ({ ...s, customer_id: "" }));
    setF(s => ({
      ...s,
      customer_id: c.id,
      customer_name: c.name,
      customer_phone: c.phone || s.customer_phone,
      customer_gstin: c.gstin || s.customer_gstin,
    }));
  };

  // Live-compute customer_freight preview when freight_type is unit-based
  const computedFreight = (() => {
    if (f.freight_type && f.freight_type !== "lumpsum" && f.unit_price && f.units) {
      return (Number(f.unit_price) * Number(f.units)).toFixed(2);
    }
    return null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_id) { toast.error("Please pick a customer with an active contract."); return; }
    if (!f.consignor_id) { toast.error("Please pick a consignor — create one under Customers if the list is empty."); return; }
    if (!f.consignee_id) { toast.error("Please pick a consignee — create one under Customers if the list is empty."); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        weight_kg: Number(f.weight_kg || 0),
        customer_freight: Number(f.customer_freight || 0),
        unit_price: Number(f.unit_price || 0),
        units: Number(f.units || 0),
        lorry_hire_total: Number(f.lorry_hire_total || 0),
        lorry_advance: Number(f.lorry_advance || 0),
        driver_hire_total: Number(f.driver_hire_total || 0),
        own_vehicle_id: vehicleMode === "own" ? (f.own_vehicle_id || null) : null,
        outsourced_vehicle_number: vehicleMode === "outsourced" ? (f.outsourced_vehicle_number || null) : null,
        outsourced_lorry_owner_name: vehicleMode === "outsourced" ? f.outsourced_lorry_owner_name : null,
        outsourced_lorry_owner_phone: vehicleMode === "outsourced" ? f.outsourced_lorry_owner_phone : null,
        driver_id: f.driver_id || null,
      };
      const { data } = await api.post("/trips", payload);
      toast.success(`Trip created · ${data.trip.cn_number}`);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A] max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="new-trip-help">
      <DialogHeader>
        <DialogTitle className="font-display font-black text-2xl">Create trip / Consignment Note</DialogTitle>
        <p id="new-trip-help" className="text-xs text-zinc-500 mt-1">Pick an active-contract customer, choose freight type, add e-way bill, vehicle and driver.</p>
      </DialogHeader>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="new-trip-form">
        <Fld label="Trip Type">
          <Select value={f.trip_type} onValueChange={(v) => setF((s) => ({ ...s, trip_type: v }))}>
            <SelectTrigger data-testid="trip-type-select" className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="vehicle_placement">Vehicle Placement (freight)</SelectItem>
              <SelectItem value="driver_placement">Driver Placement (driver-only hire)</SelectItem>
            </SelectContent>
          </Select>
        </Fld>

        <Fld label="Customer (active contract) *">
          <Select value={f.customer_id} onValueChange={onCustomerPick}>
            <SelectTrigger data-testid="trip-customer-select" className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]">
              <SelectValue placeholder={activeCustomers.length ? "Pick customer" : "No active contracts — add one first"} />
            </SelectTrigger>
            <SelectContent>
              {activeCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.gstin || "no GST"}</SelectItem>)}
            </SelectContent>
          </Select>
          {!activeCustomers.length && (
            <div className="text-[11px] text-amber-400 mt-1">
              → <Link to="/customers" className="underline">Create a customer</Link> with an active contract to continue.
            </div>
          )}
        </Fld>

        <Fld label="Customer phone"><I v={f.customer_phone} k="customer_phone" set={set} /></Fld>
        <Fld label="Customer GSTIN"><I v={f.customer_gstin} k="customer_gstin" set={set} /></Fld>

        <div className="sm:col-span-2 border-t border-[#2A2A2A] pt-4">
          <div className="overline text-[#FF5A00] mb-3">Consignor / Consignee (from customer master)</div>
          {!f.customer_id && <div className="text-xs text-zinc-500 mb-2">Pick a customer first — their saved consignors/consignees will appear below.</div>}
          {f.customer_id && (() => {
            const c = (customers || []).find(x => x.id === f.customer_id);
            const cons = (c?.consignors || []);
            const cnsg = (c?.consignees || []);
            const autoSelect = (type, p) => {
              if (type === "consignor") setF(s => ({ ...s, consignor_id: p.id, consignor: p.name || "", consignor_address: p.address || "", consignor_gstin: p.gstin || "" }));
              else setF(s => ({ ...s, consignee_id: p.id, consignee: p.name || "", consignee_address: p.address || "", consignee_gstin: p.gstin || "" }));
            };
            return (
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Consignor *">
                  <div className="flex gap-2">
                    <Select value={f.consignor_id || ""} onValueChange={(v) => {
                      const p = cons.find(x => x.id === v);
                      setF(s => ({ ...s, consignor_id: v, consignor: p?.name || "", consignor_address: p?.address || "", consignor_gstin: p?.gstin || "" }));
                    }}>
                      <SelectTrigger data-testid="trip-consignor-select" className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00] flex-1">
                        <SelectValue placeholder={cons.length ? "Pick consignor" : "No consignors — click +"} />
                      </SelectTrigger>
                      <SelectContent>
                        {cons.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.gstin ? `· ${p.gstin}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <InlineAddParty customerId={f.customer_id} type="consignor" onCreated={(p) => autoSelect("consignor", p)} onCustomersRefresh={onCustomersRefresh} />
                  </div>
                </Fld>
                <Fld label="Consignee *">
                  <div className="flex gap-2">
                    <Select value={f.consignee_id || ""} onValueChange={(v) => {
                      const p = cnsg.find(x => x.id === v);
                      setF(s => ({ ...s, consignee_id: v, consignee: p?.name || "", consignee_address: p?.address || "", consignee_gstin: p?.gstin || "" }));
                    }}>
                      <SelectTrigger data-testid="trip-consignee-select" className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00] flex-1">
                        <SelectValue placeholder={cnsg.length ? "Pick consignee" : "No consignees — click +"} />
                      </SelectTrigger>
                      <SelectContent>
                        {cnsg.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.gstin ? `· ${p.gstin}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <InlineAddParty customerId={f.customer_id} type="consignee" onCreated={(p) => autoSelect("consignee", p)} onCustomersRefresh={onCustomersRefresh} />
                  </div>
                </Fld>
                {f.consignor_id && (
                  <div className="sm:col-span-2 p-2 bg-[#0A0A0A] border border-dashed border-emerald-500/30 rounded-sm text-xs text-zinc-400">
                    <span className="font-bold text-emerald-400">Consignor:</span> {f.consignor} · GSTIN {f.consignor_gstin || "—"} · {f.consignor_address || "no address"}
                  </div>
                )}
                {f.consignee_id && (
                  <div className="sm:col-span-2 p-2 bg-[#0A0A0A] border border-dashed border-sky-500/30 rounded-sm text-xs text-zinc-400">
                    <span className="font-bold text-sky-400">Consignee:</span> {f.consignee} · GSTIN {f.consignee_gstin || "—"} · {f.consignee_address || "no address"}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <Fld label="Origin city"><I required v={f.source_city} k="source_city" set={set} /></Fld>
        <Fld label="Destination city"><I required v={f.destination_city} k="destination_city" set={set} /></Fld>
        <Fld label="Material"><I v={f.material} k="material" set={set} /></Fld>
        <Fld label="Weight (kg)"><I t="number" v={f.weight_kg} k="weight_kg" set={set} /></Fld>
        <Fld label="Sales order #"><I v={f.sales_order_number} k="sales_order_number" set={set} /></Fld>
        <Fld label="Customer invoice #"><I v={f.invoice_number} k="invoice_number" set={set} /></Fld>
        <Fld label="Pickup date"><I t="date" v={f.pickup_date} k="pickup_date" set={set} /></Fld>
        <Fld label="Delivery date"><I t="date" v={f.delivery_date} k="delivery_date" set={set} /></Fld>

        <div className="sm:col-span-2 border-t border-[#2A2A2A] pt-4">
          <div className="overline text-[#FF5A00] mb-3">E-way Bill</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Fld label="E-way bill number"><I v={f.eway_bill_number} k="eway_bill_number" set={set} placeholder="EWB__ __ __ __ __ __ __ __ __ __ __" /></Fld>
            <Fld label="E-way expiry date"><I t="date" v={f.eway_bill_expiry} k="eway_bill_expiry" set={set} /></Fld>
          </div>
        </div>

        <div className="sm:col-span-2 border-t border-[#2A2A2A] pt-4">
          <div className="overline text-[#FF5A00] mb-3">Vehicle</div>
          <div className="flex gap-2 mb-3">
            <button type="button" onClick={() => setVehicleMode("own")} data-testid="vehicle-mode-own"
              className={`px-3 py-1 text-xs uppercase tracking-wider border rounded-sm ${vehicleMode === "own" ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-[#2A2A2A] text-zinc-400"}`}>Own Fleet</button>
            <button type="button" onClick={() => setVehicleMode("outsourced")} data-testid="vehicle-mode-outsourced"
              className={`px-3 py-1 text-xs uppercase tracking-wider border rounded-sm ${vehicleMode === "outsourced" ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-[#2A2A2A] text-zinc-400"}`}>Outsourced Lorry</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {vehicleMode === "own" ? (
              <Fld label="Own vehicle">
                <Select value={f.own_vehicle_id} onValueChange={(v) => setF((s) => ({ ...s, own_vehicle_id: v }))}>
                  <SelectTrigger data-testid="own-vehicle-select" className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue placeholder="Choose vehicle" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.vehicle_number} · {v.vehicle_type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Fld>
            ) : (
              <>
                <Fld label="Vehicle number"><I v={f.outsourced_vehicle_number} k="outsourced_vehicle_number" set={set} placeholder="MH12AB1234" /></Fld>
                <Fld label="Lorry owner name"><I v={f.outsourced_lorry_owner_name} k="outsourced_lorry_owner_name" set={set} /></Fld>
                <Fld label="Lorry owner phone"><I v={f.outsourced_lorry_owner_phone} k="outsourced_lorry_owner_phone" set={set} /></Fld>
              </>
            )}
          </div>
        </div>

        <div className="sm:col-span-2 border-t border-[#2A2A2A] pt-4">
          <div className="overline text-[#FF5A00] mb-3">Driver</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Fld label="Pick registered driver (optional)">
              <Select value={f.driver_id} onValueChange={(v) => setF((s) => ({ ...s, driver_id: v }))}>
                <SelectTrigger data-testid="trip-driver-select" className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue placeholder="Choose driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.filter(d => !d.blacklisted).map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {d.phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Or type name"><I v={f.driver_name} k="driver_name" set={set} /></Fld>
            <Fld label="Driver phone"><I v={f.driver_phone} k="driver_phone" set={set} /></Fld>
            <Fld label="Driver DL #"><I v={f.driver_dl} k="driver_dl" set={set} /></Fld>
          </div>
        </div>

        <div className="sm:col-span-2 border-t border-[#2A2A2A] pt-4">
          <div className="overline text-[#FF5A00] mb-3">Freight / Money</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Fld label="Freight type">
              <Select value={f.freight_type} onValueChange={(v) => setF((s) => ({ ...s, freight_type: v }))}>
                <SelectTrigger data-testid="freight-type-select" className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lumpsum">Lumpsum (direct ₹ amount)</SelectItem>
                  <SelectItem value="per_mt">Per Metric Tonne</SelectItem>
                  <SelectItem value="per_kg">Per Kg</SelectItem>
                  <SelectItem value="per_km">Per Km</SelectItem>
                  <SelectItem value="per_machine">Per Machine</SelectItem>
                  <SelectItem value="per_bus">Per Bus</SelectItem>
                  <SelectItem value="per_car">Per Car</SelectItem>
                  <SelectItem value="per_truck">Per Truck</SelectItem>
                  <SelectItem value="per_unit">Per Unit</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
            {f.freight_type !== "lumpsum" ? (
              <>
                <Fld label="Unit price (₹)"><I t="number" v={f.unit_price} k="unit_price" set={set} /></Fld>
                <Fld label="Units"><I t="number" v={f.units} k="units" set={set} /></Fld>
                <Fld label="Computed freight (₹)">
                  <div className="h-10 flex items-center px-3 bg-[#141414] border border-dashed border-[#FF5A00]/50 rounded-sm font-mono text-[#FF5A00] text-sm" data-testid="computed-freight">
                    {computedFreight ? formatINR(Number(computedFreight)) : "—"}
                  </div>
                </Fld>
              </>
            ) : (
              <Fld label="Customer freight (₹)"><I t="number" required v={f.customer_freight} k="customer_freight" set={set} /></Fld>
            )}
            <Fld label="Lorry hire total (₹)"><I t="number" v={f.lorry_hire_total} k="lorry_hire_total" set={set} /></Fld>
            <Fld label="Lorry advance (₹)"><I t="number" v={f.lorry_advance} k="lorry_advance" set={set} /></Fld>
            <Fld label="Driver hire total (₹)"><I t="number" v={f.driver_hire_total} k="driver_hire_total" set={set} /></Fld>
          </div>
        </div>

        <Fld label="Remarks" span2>
          <Textarea value={f.remarks} onChange={set("remarks")} data-testid="trip-remarks-input" rows={2}
            className="bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
        </Fld>

        <DialogFooter className="sm:col-span-2">
          <Button type="submit" disabled={busy} data-testid="submit-trip-btn"
            className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-11 px-6">
            {busy ? "Creating..." : "Create Trip (Draft)"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Fld({ label, span2, children }) {
  return (
    <div className={span2 ? "sm:col-span-2" : ""}>
      <Label className="overline text-zinc-400">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function I({ t = "text", v, k, set, required, placeholder }) {
  return <Input type={t} required={required} value={v} placeholder={placeholder}
    onChange={set(k)} data-testid={`trip-${k}-input`}
    className="h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />;
}

function InlineAddParty({ customerId, type, onCreated, onCustomersRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", gstin: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/parties`, { type, ...form });
      toast.success(`${type} added`);
      await onCustomersRefresh?.();
      onCreated?.(data.party);
      setForm({ name: "", gstin: "", phone: "", address: "" });
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" data-testid={`inline-add-${type}-btn`}
          className="h-10 px-3 rounded-sm border-[#FF5A00]/40 bg-transparent text-[#FF5A00] hover:bg-[#FF5A00]/10 shrink-0">
          <Plus className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-[#2A2A2A] sm:max-w-md" data-testid={`inline-${type}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">Add {type}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <div>
            <Label className="overline text-zinc-500">Name *</Label>
            <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid={`inline-${type}-name`} className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm" />
          </div>
          <div>
            <Label className="overline text-zinc-500">GSTIN</Label>
            <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              data-testid={`inline-${type}-gstin`} className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm font-mono" />
          </div>
          <div>
            <Label className="overline text-zinc-500">Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid={`inline-${type}-phone`} className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm" />
          </div>
          <div>
            <Label className="overline text-zinc-500">Address</Label>
            <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid={`inline-${type}-address`} className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}
              className="rounded-sm border-[#2A2A2A] bg-transparent">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid={`inline-${type}-save`}
              className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
              {busy ? "Saving…" : `Save & select`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
